import {
  GridColDef,
  GridEditBooleanCell,
  GridEditDateCell,
  GridEditInputCell,
  GridEditSingleSelectCell,
} from '@mui/x-data-grid-premium';
import { useMemo } from 'react';

import { DataGridCRUDColumnsValidation } from '~/components/SolaForceDataGrid/hooks/useDataGridCRUDValidation';

type PropsType<T> = {
  columns: GridColDef<T>[];
  columnsValidation: DataGridCRUDColumnsValidation<T>;
};

type UseDataGridCRUDColumnsOutput<T> = {
  columns: GridColDef<T>[];
};

export const useDataGridCRUDColumns = <T extends object>(
  props: PropsType<T>,
): UseDataGridCRUDColumnsOutput<T> => {
  const {
    columns,
    columnsValidation: {
      renderEditCellWithErrorTooltip,
      getEditCellErrorClassName,
      onPreProcessEditCell,
    },
  } = props;

  const crudColumns = useMemo(() => {
    const wrapErrorTooltip = (column: GridColDef<T>): GridColDef<T> => {
      const { renderEditCell, ...columnProps } = column;
      if (!!renderEditCell) {
        return {
          ...columnProps,
          renderEditCell: renderEditCellWithErrorTooltip(renderEditCell),
        };
      }

      if (columnProps.editable && columnProps.type) {
        return {
          ...columnProps,
          renderEditCell: renderEditCellWithErrorTooltip((params) => {
            switch (columnProps.type) {
              case 'singleSelect':
                return <GridEditSingleSelectCell {...params} />;
              case 'date':
              case 'dateTime':
                return <GridEditDateCell {...params} />;
              case 'boolean':
                return <GridEditBooleanCell {...params} />;
              case 'string':
              case 'number':
              default:
                return <GridEditInputCell {...params} />;
            }
          }),
        };
      }

      return column;
    };

    const includeErrorCellClassName = (
      column: GridColDef<T>,
    ): GridColDef<T> => {
      const { editable, cellClassName } = column;

      if (!!editable && !cellClassName) {
        return {
          ...column,
          cellClassName: getEditCellErrorClassName,
        };
      }

      return column;
    };

    const includeOnPreProcessEditCell = (
      column: GridColDef<T>,
    ): GridColDef<T> => {
      const { editable, preProcessEditCellProps, field } = column;
      if (editable && !preProcessEditCellProps) {
        return {
          ...column,
          preProcessEditCellProps: onPreProcessEditCell(field),
        };
      }

      return column;
    };

    return columns.map((column) => {
      return wrapErrorTooltip(
        includeErrorCellClassName(includeOnPreProcessEditCell(column)),
      );
    });
  }, [
    columns,
    renderEditCellWithErrorTooltip,
    getEditCellErrorClassName,
    onPreProcessEditCell,
  ]);

  return {
    columns: crudColumns,
  };
};
